import {
    type Goal,
    type GoalGroup,
    GoalStatus,
    GoalTaskStatus,
} from '@fruitful/types';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import capitalize from 'lodash/capitalize';
import { Button as PRButton } from 'primereact/button';
import { Column, type ColumnEditorOptions } from 'primereact/column';
import {
    DataTable,
    type DataTableRowEditCompleteEvent,
    type DataTableRowEvent,
    type DataTableRowReorderEvent,
} from 'primereact/datatable';
import { Dropdown, type DropdownChangeEvent } from 'primereact/dropdown';
import React, { useEffect, useState } from 'react';
import {
    BanknoteIcon,
    BlocksIcon,
    BodyMediumRegular,
    BodySmallRegular,
    FeatherIcon,
    FlagIcon,
    Icon,
    type IconType,
    PiggyBankIcon,
    PlusIcon,
    XIcon,
    XStack,
    YStack,
} from 'smoothie';
import { isEmpty } from 'smoothie/src/misc';

import {
    AddNewTextEditor,
    AmountEditor,
    DateTextEditor,
    TextEditor,
} from '../../../../common/components/GridCellEditors';
import { MonospaceNumericText } from '../../../../common/components/GridCellRenderers';
import {
    formatDollars,
    formatToSimpleDateString,
} from '../../../../common/utils/formatting';
import {
    adminUserAtom,
    allExpandedGoalsAtom,
    createdGoalAtom,
    mutatedGoalAtom,
    newGoalToExpandAtom,
    updatedGoalsAtom,
} from '../../../entityAtoms';
import { type GoalCategory } from '../../../entityTypes';
import { GoalTasksRenderer } from './GoalTasks';

enum GoalCategoryDescription {
    FUNDAMENTALS = 'Fundamentals',
    MONEY_MANAGEMENT = 'Money Management',
    BUILDING_WEALTH = 'Building Wealth',
    MILESTONES = 'Milestones',
    LEGACY = 'Legacy',
}

const GoalCategoryColors = {
    [GoalCategoryDescription.FUNDAMENTALS]: '#FFF6EC',
    [GoalCategoryDescription.MONEY_MANAGEMENT]: '#FCF1CB',
    [GoalCategoryDescription.BUILDING_WEALTH]: '#FBE4EB',
    [GoalCategoryDescription.MILESTONES]: '#ECFDF3',
    [GoalCategoryDescription.LEGACY]: '#DFF0FF',
};

const GoalCategoryIcons: Record<GoalCategoryDescription, IconType> = {
    [GoalCategoryDescription.FUNDAMENTALS]: BlocksIcon,
    [GoalCategoryDescription.MONEY_MANAGEMENT]: BanknoteIcon,
    [GoalCategoryDescription.BUILDING_WEALTH]: PiggyBankIcon,
    [GoalCategoryDescription.MILESTONES]: FlagIcon,
    [GoalCategoryDescription.LEGACY]: FeatherIcon,
};

type GoalGroupId = string;
type GoalGroupToCategoryDictionary = Record<
    GoalGroupId,
    GoalCategory | undefined
>;

const NEW_PREFIX = 'new';
const SEPARATOR = '++';

const getAllGoalsOfStatus = (goalGroups: GoalGroup[], status: GoalStatus) => {
    const goals = goalGroups
        .sort((a, b) => {
            return a.position - b.position; // sort groups by their position
        })
        .map((group) => group.goals.filter((goal) => goal.status === status)) // get array of goal arrays
        .flat(); // flatten to one array of goals
    return sortGoals(goals);
};

const sortGoals = (goals: Goal[]) => {
    return goals
        .sort((a, b) => {
            return a.position - b.position; // sort goals across groups by their position
        })
        .map((goal, index) => {
            goal.position = index; // overwrite position on client side to match order
            return goal;
        });
};

export const GoalsRenderer = ({
    goalGroups,
    goalCategories,
    status,
}: {
    goalGroups: GoalGroup[];
    goalCategories: GoalCategory[];
    status: GoalStatus;
}) => {
    const adminUser = useAtomValue(adminUserAtom);
    const canEditGoals =
        adminUser.authorization.selectedMembership.goals.canEdit;
    const setMutatedGoal = useSetAtom(mutatedGoalAtom);
    const setUpdatedGoals = useSetAtom(updatedGoalsAtom);
    const [goalRows, setGoalRows] = useState<Goal[]>(
        getAllGoalsOfStatus(goalGroups, status),
    );

    useEffect(() => {
        setGoalRows(getAllGoalsOfStatus(goalGroups, status));
    }, [goalGroups, status]);

    const [goalGroupToCategoryDictionary, setGoalGroupToCategoryDictionary] =
        useState<GoalGroupToCategoryDictionary>({});
    useEffect(() => {
        const dict: GoalGroupToCategoryDictionary = {};
        goalCategories.forEach((category) => {
            const goalGroupId = goalGroups.find(
                (group) => group.goalCategoryId === category.id,
            )?.id;
            if (goalGroupId !== undefined) {
                dict[goalGroupId] = category;
            } else {
                const tempGoalGroupId = encodeNewGoalGroupTempId(category.id);
                dict[tempGoalGroupId] = category;
            }
        });
        setGoalGroupToCategoryDictionary(dict);
    }, [goalGroups, goalCategories]);

    const [allExpandedRows, setAllExpandedRows] = useAtom(allExpandedGoalsAtom);
    const [newGoalToExpand, setNewGoalToExpand] = useAtom(newGoalToExpandAtom);
    useEffect(() => {
        if (newGoalToExpand !== null) {
            setAllExpandedRows((prev) => {
                return {
                    ...prev,
                    [newGoalToExpand]: true,
                };
            });
            setNewGoalToExpand(null);
        }
    }, [newGoalToExpand]);

    const rowExpansionTemplate = (goal: Goal) => {
        return (
            <GoalTasksRenderer
                goal={goal}
                onGoalStatusChange={onGoalStatusChange}
            />
        );
    };

    const onGoalStatusChange = (updatedGoal: Goal) => {
        // updatedGoal should have a new status, different from the status of all goals in goalRows
        // So, get the list of goals with the same status as the new status
        // and make sure updatedGoal will be at the bottom of that list
        updatedGoal.position = getAllGoalsOfStatus(
            goalGroups,
            updatedGoal.status,
        ).length;

        setMutatedGoal(updatedGoal);
        setGoalRows((prev) => [
            ...prev.filter((goal) => goal.id !== updatedGoal.id),
        ]);
    };

    const onRowReorder = ({
        value: rowsInNewOrder,
    }: DataTableRowReorderEvent<Goal[]>) => {
        const reorderedGoals = rowsInNewOrder.map((goal, index) => {
            goal.position = index;
            return goal;
        });
        setUpdatedGoals(reorderedGoals);
        setGoalRows(reorderedGoals);
    };

    const onRowEditComplete = ({
        index,
        newData,
    }: DataTableRowEditCompleteEvent) => {
        const updatedGoal = newData as Goal;
        setMutatedGoal(updatedGoal);
        setGoalRows((prev: Goal[]) => {
            const goals = [...prev];
            goals[index] = updatedGoal;
            return goals;
        });
    };

    return (
        <YStack>
            {goalRows.length > 1 && (
                <XStack gap={2} marginBottom={4}>
                    <PRButton
                        label="Expand all"
                        text
                        size="small"
                        severity="secondary"
                        onClick={() => {
                            setAllExpandedRows((prev) => {
                                const toExpand = goalRows.reduce(
                                    (acc, goal) => {
                                        acc[goal.id] = true;
                                        return acc;
                                    },
                                    {},
                                );
                                return { ...prev, ...toExpand };
                            });
                        }}
                    />
                    <PRButton
                        label="Collapse all"
                        text
                        size="small"
                        severity="secondary"
                        onClick={() => {
                            setAllExpandedRows((prev) => {
                                goalRows.forEach((goal) => {
                                    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                                    prev[goal.id] && delete prev[goal.id];
                                });
                                return { ...prev };
                            });
                        }}
                    />
                </XStack>
            )}
            <DataTable
                dataKey="id"
                value={goalRows}
                size="small"
                className="datatable-without-right-aligned-margin"
                tableStyle={{ width: '100%' }}
                reorderableRows
                onRowReorder={onRowReorder}
                rowExpansionTemplate={rowExpansionTemplate}
                expandedRows={allExpandedRows}
                onRowExpand={(e: DataTableRowEvent) => {
                    setAllExpandedRows((prev) => {
                        return {
                            ...prev,
                            [(e.data as Goal).id]: true,
                        };
                    });
                }}
                onRowCollapse={(e: DataTableRowEvent) => {
                    setAllExpandedRows((prev) => {
                        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                        delete prev[(e.data as Goal).id];
                        return { ...prev };
                    });
                }}
                editMode="row"
                onRowEditComplete={onRowEditComplete}
                showHeaders={goalRows.length > 0}
                emptyMessage={<XStack height={0} />}
            >
                <Column expander={true} style={{ width: '4rem' }} />
                <Column
                    field="goalGroupId"
                    header="Group"
                    style={{ width: '4rem' }}
                    body={(goal: Goal) => (
                        <GroupCategoryRenderer
                            goalGroupId={goal.goalGroupId}
                            goalGroupToCategoryDictionary={
                                goalGroupToCategoryDictionary
                            }
                        />
                    )}
                    editor={(options) =>
                        GroupEditor(options, goalGroupToCategoryDictionary)
                    }
                />
                <Column
                    field="description"
                    header="Goal"
                    editor={(options) => <TextEditor options={options} />}
                />
                <Column
                    header="Tasks"
                    align="right"
                    style={{ width: '4rem' }}
                    body={TasksSummaryRenderer}
                />
                <Column
                    field="targetAmount"
                    align="right"
                    header="Target amount"
                    style={{ width: '10rem' }}
                    editor={(options) => <AmountEditor options={options} />}
                    body={AmountRenderer}
                />
                <Column
                    field="targetDate"
                    align="right"
                    header="Target date"
                    style={{ width: '7rem' }}
                    editor={(options) => TargetDateEditor(options)}
                    body={DateRenderer}
                />
                <Column
                    rowEditor
                    style={{ width: '3rem' }}
                    hidden={!canEditGoals}
                />
                <Column
                    rowReorder
                    style={{ width: '3rem' }}
                    hidden={!canEditGoals}
                />
            </DataTable>

            {[GoalStatus.DRAFT, GoalStatus.IN_PROGRESS].includes(status) &&
                canEditGoals && (
                    <AddNewGoal
                        goalStatus={status}
                        goalGroupToCategoryDictionary={
                            goalGroupToCategoryDictionary
                        }
                        currentGoalCount={goalRows.length}
                        onAddNewGoal={(newGoal: Goal) => {
                            setGoalRows((prev) => {
                                return [...prev, newGoal];
                            });
                        }}
                    />
                )}
        </YStack>
    );
};

const GroupCategoryRenderer = ({
    goalGroupId,
    goalGroupToCategoryDictionary,
}: {
    goalGroupId: string;
    goalGroupToCategoryDictionary: GoalGroupToCategoryDictionary;
}) => {
    const goalCategory = goalGroupToCategoryDictionary[goalGroupId];
    const categoryDescription = goalCategory?.description as
        | GoalCategoryDescription
        | undefined;
    if (goalCategory === undefined || categoryDescription === undefined)
        return null;

    return (
        <PRButton // Not really a button but this is an easy way to get an icon with a tooltip
            rounded
            text
            tooltip={capitalize(categoryDescription)}
            style={{
                backgroundColor: GoalCategoryColors[categoryDescription],
                padding: 8,
            }}
        >
            <Icon icon={GoalCategoryIcons[categoryDescription]} />
        </PRButton>
    );
};

const TasksSummaryRenderer = ({ tasks }: Goal) => {
    tasks = tasks.filter((task) => task.status !== GoalTaskStatus.DELETED);
    if (tasks.length === 0) return null;

    const tasksCount = tasks.length;
    const completedTasksCount = tasks.filter(
        (task) => task.status === GoalTaskStatus.COMPLETE,
    ).length;
    const color =
        completedTasksCount === tasksCount ? '$green3000' : '$gray900';

    return (
        <BodySmallRegular color={color}>
            {completedTasksCount} / {tasks.length}
        </BodySmallRegular>
    );
};

const AmountRenderer = ({ targetAmount }: Goal) =>
    targetAmount == null ? null : (
        <MonospaceNumericText>
            {formatDollars(targetAmount, false)}
        </MonospaceNumericText>
    );

const DateRenderer = ({ targetDate }: Goal) =>
    targetDate === undefined ? null : (
        <BodyMediumRegular>
            {formatToSimpleDateString(targetDate)}
        </BodyMediumRegular>
    );

const GroupEditor = (
    options:
        | ColumnEditorOptions
        | {
              value: string;
              rowData: Goal;
              editorCallback: (value: string) => void;
          },
    goalGroupToCategoryDictionary: GoalGroupToCategoryDictionary,
) => {
    return (
        <GroupDropdown
            value={options.value as string}
            onChange={options.editorCallback as (value: string) => void}
            goalGroupToCategoryDictionary={goalGroupToCategoryDictionary}
        />
    );
};

const TargetDateEditor = (options: ColumnEditorOptions) => {
    return (
        <DateTextEditor
            options={options}
            calendarProps={{
                minDate: new Date('2024-01-01'),
            }}
        />
    );
};

interface Option {
    label: string;
    value: string;
}

const GroupDropdown = ({
    value: goalGroupId,
    onChange,
    goalGroupToCategoryDictionary,
}: {
    value: string;
    onChange: (value: string) => void;
    goalGroupToCategoryDictionary: GoalGroupToCategoryDictionary;
}) => {
    const options: Option[] = Object.entries(goalGroupToCategoryDictionary).map(
        ([goalGroupId, category]) => ({
            label: category?.description ?? 'Other',
            value: goalGroupId,
        }),
    );
    const optionRenderer = (option: Option) => (
        <XStack gap={8} alignItems="center" marginVertical={-8}>
            <GroupCategoryRenderer
                goalGroupId={option.value}
                goalGroupToCategoryDictionary={goalGroupToCategoryDictionary}
            />
            <BodySmallRegular>{option.label}</BodySmallRegular>
        </XStack>
    );
    goalGroupId = !isEmpty(goalGroupId)
        ? goalGroupId
        : (options[options.length]?.value ?? '');

    return (
        <Dropdown
            value={goalGroupId}
            onChange={(e: DropdownChangeEvent) => {
                onChange(e.value as string);
            }}
            options={options}
            itemTemplate={optionRenderer}
            valueTemplate={() => (
                <XStack margin={-8}>
                    <GroupCategoryRenderer
                        goalGroupId={goalGroupId}
                        goalGroupToCategoryDictionary={
                            goalGroupToCategoryDictionary
                        }
                    />
                </XStack>
            )}
            placeholder="Goal Category"
            checkmark={true}
            highlightOnSelect={false}
            scrollHeight="250px"
        />
    );
};

const AddNewGoal = ({
    goalGroupToCategoryDictionary,
    goalStatus,
    currentGoalCount,
    onAddNewGoal,
}: {
    goalGroupToCategoryDictionary: GoalGroupToCategoryDictionary;
    goalStatus: GoalStatus;
    currentGoalCount: number;
    onAddNewGoal: (newGoal: Goal) => void;
}) => {
    const setCreatedGoal = useSetAtom(createdGoalAtom);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [description, setDescription] = useState('');
    const [goalGroupId, setGoalGroupId] = useState('');
    const reset = () => {
        setDescription('');
        setGoalGroupId('');
        setIsAddingNew(false);
    };
    const onPressSubmit = () => {
        if (description.trim() === '') {
            window.alert('Goal description cannot be empty');
            return;
        }
        if (goalGroupId.trim() === '') {
            window.alert('A Goal Category must be selected');
            return;
        }

        const newGoal: Goal = {
            id: encodeNewGoalTempId(currentGoalCount),
            goalGroupId,
            description,
            position: currentGoalCount,
            status: goalStatus,
            createdAt: new Date().toISOString(),
            tasks: [],
        };
        onAddNewGoal(newGoal);
        setCreatedGoal(newGoal);
        reset();
    };

    return (
        <XStack
            alignItems="center"
            paddingHorizontal={8}
            paddingTop={4}
            gap={12}
            width="100%"
        >
            <XStack flexGrow={1} alignItems="center">
                <XStack
                    width="4rem"
                    padding={8}
                    onPress={() => {
                        setIsAddingNew(true);
                    }}
                    hoverStyle={{ cursor: 'pointer' }}
                >
                    <Icon icon={PlusIcon} color="$gray800" />
                </XStack>
                {isAddingNew ? (
                    <XStack alignItems="center" gap={8} width="80%">
                        <AddNewTextEditor
                            value={description}
                            onChange={setDescription}
                        />
                        <GroupDropdown
                            value={goalGroupId}
                            onChange={setGoalGroupId}
                            goalGroupToCategoryDictionary={
                                goalGroupToCategoryDictionary
                            }
                        />
                        <PRButton
                            label="Add"
                            size="small"
                            severity="success"
                            outlined
                            onClick={onPressSubmit}
                        />
                        <PRButton
                            size="small"
                            severity="secondary"
                            outlined
                            onClick={reset}
                        >
                            <Icon icon={XIcon} />
                        </PRButton>
                    </XStack>
                ) : (
                    <BodyMediumRegular
                        width="60%"
                        color="$gray800"
                        paddingVertical={10}
                        paddingHorizontal={8}
                        marginLeft={-8}
                        borderRadius={6}
                        borderWidth={1}
                        borderColor="transparent"
                        hoverStyle={{
                            cursor: 'text',
                            borderColor: '$gray300',
                        }}
                        onPress={() => {
                            setIsAddingNew(true);
                        }}
                    >
                        New Goal
                    </BodyMediumRegular>
                )}
            </XStack>
        </XStack>
    );
};

const encodeNewGoalTempId = (position) => {
    return `${NEW_PREFIX}${SEPARATOR}${position}`;
};

const encodeNewGoalGroupTempId = (goalCategoryId) => {
    return `${NEW_PREFIX}${SEPARATOR}${goalCategoryId}`;
};

export const decodeNewGoalGroupTempId = (
    tempId: string,
): { goalCategoryId: string } | undefined => {
    const [prefix, goalCategoryId = ''] = tempId.split(SEPARATOR);
    return prefix !== NEW_PREFIX ? undefined : { goalCategoryId };
};

export const newGoalGroupNeeded = (tempId: string): boolean => {
    return decodeNewGoalGroupTempId(tempId) !== undefined;
};
